import { React, useState, useEffect } from "react";
import { useStateValue } from "../../../store/StateProvider";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import dataSourceAggregator from "../../Integrations/dataSourceAggregator";
import { actionTypes } from "../../../utils/constants";
import constants from "../../../config/constants";
import { ReactComponent as IconSearch } from "../../../static/svgs/icon-seach.svg";

const SelectSearchControl = ({ fieldConfig, fieldValidator, hasError, setFirstLoad, firstLoad, showErrors }) => {
  const { t } = useTranslation();
  const [{ country, currentStep, formValues, countryOfRegId, registrationCountry, confirmedValues }, dispatch] =
    useStateValue();
  let dataSource = fieldConfig.dataSource;
  dataSource.globalStateValues = {
    country,
    registrationCountry,
    countryOfRegId,
    currentStep,
    ...formValues,
    ...confirmedValues
  };
  const defaultOption = dataSource.globalStateValues[fieldConfig.name] ?? [];

  const [fieldValue, setFieldValue] = useState(defaultOption);
  const [options, setOptions] = useState(fieldConfig.options ?? []);
  const [validators, setValidators] = useState(fieldConfig.validators);
  const [searchText, setSearchText] = useState("");

  const referencedData =
    fieldConfig.dependencyField !== undefined ? formValues[fieldConfig.dependencyField] : formValues;
  let fieldClass = "input_plain ";
  fieldClass += fieldConfig.class;
  if (hasError && (!firstLoad || showErrors)) {
    fieldClass += " has_error is-invalid";
  }

  const checkRemoteValidations = () => {
    if (fieldConfig.remoteValidation) {
      dispatch({
        type: actionTypes.SET_FIELD_MUST_RUN_VALIDATIONS,
        payload: fieldConfig.remoteValidation
      });
    }
  };

  const handleBlur = () => {
    setFirstLoad(false);
    fieldValidator(validators);
  };

  useEffect(() => {
    const loadOptions = async () => {
      if (fieldConfig.dependencyField !== undefined && referencedData !== undefined && referencedData !== null) {
        fieldConfig.dataSource.globalStateValues[fieldConfig.dependencyField] = referencedData;
      }
      const sourceData = await dataSourceAggregator(fieldConfig.dataSource);
      if (sourceData !== undefined && sourceData !== "" && sourceData !== null && sourceData.length > 0) {
        const formattedOptions = sourceData.map(item => ({
          value: item.id || item.value,
          label: item.name || item.label,
          id: item.id || item.value
        }));
        setOptions(formattedOptions);

        setValidators(prev =>
          prev.map(validator => {
            if (validator.type === constants.VALIDATOR_VALID_OPTION) {
              validator.options = sourceData;
            }
            return validator;
          })
        );

        if (
          fieldValue &&
          !isEmpty(fieldValue) &&
          !isEmpty(formattedOptions) &&
          !formattedOptions.some(item => item.value === fieldValue.value) &&
          fieldConfig.dependencyField !== undefined
        ) {
          setFieldValue(undefined);
          dispatch({
            type: actionTypes.PRESERVE_GLOBAL_STATE,
            payload: { [fieldConfig.id]: undefined }
          });
          return;
        }

        if (firstLoad && defaultOption !== undefined && defaultOption !== null) {
          setFieldValue(defaultOption);
        }
      }
    };

    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referencedData]);

  useEffect(() => {
    if (fieldValue) {
      fieldValidator(validators);
    }
  }, [fieldValue, fieldValidator, validators]);

  const handleChange = (value, option) => {
    setFirstLoad(false);
    setFieldValue(option);
    dispatch({
      type: actionTypes.PRESERVE_GLOBAL_STATE,
      payload: { [fieldConfig.id]: option }
    });
    checkRemoteValidations();
  };

  const handleSearch = value => {
    setSearchText(value);
  };

  useEffect(() => {
    if (showErrors) {
      fieldValidator(validators);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showErrors]);

  const isEmpty = value => {
    return (
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "object" && value !== null && Object.keys(value).length === 0)
    );
  };

  const optionsStyleInclude = options.map(item => {
    return { ...item, className: "select-search-option-custom" };
  });

  return (
    <Select
      className={`select-search-custom ${fieldClass}`}
      value={fieldValue?.value ?? defaultOption?.value}
      onChange={handleChange}
      onSearch={handleSearch}
      onBlur={() => handleBlur()}
      placeholder={fieldConfig.placeHolder}
      disabled={fieldConfig.disabled}
      suffixIcon={<IconSearch />}
      variant="underlined"
      showSearch
      allowClear
      options={optionsStyleInclude}
      filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
      style={{ width: "100%" }}
      notFoundContent={searchText ? `"${searchText}" ${t("content.notFound")}` : t("content.withoutResults")}
    />
  );
};

export default SelectSearchControl;
